import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { searchTenantUsers } from "../services/tenantUsers/tenantUsersApi";
import { mapTenantUserToSearchResult } from "../services/tenantUsers/tenantUsersMappers";
import type { WorkspaceUserSearchResponse } from "../services/workspaces/workspaceMembers.types";
import { getActiveTenantId } from "../services/workspace/activeWorkspace";
import { queryKeys } from "../services/queryKeys";

export const MIN_TENANT_USER_SEARCH_LENGTH = 2;

export interface UseTenantUserSearchOptions {
  query: string;
  enabled?: boolean;
  limit?: number;
}

/**
 * Searches active members of the caller's current tenant, for the story
 * share picker. Returns the same `{ items, query }` shape as
 * `useWorkspaceUserSearch` (mapped via `mapTenantUserToSearchResult`) so the
 * shared invite-picker UI (`MemberSearchDropdown`, `useMemberInvitePicker`)
 * needs no change to consume tenant-scoped results.
 */
export const useTenantUserSearch = ({
  query,
  enabled = true,
  limit = 10,
}: UseTenantUserSearchOptions) => {
  const trimmed = query.trim();
  const tenantId = getActiveTenantId();

  return useQuery<WorkspaceUserSearchResponse>({
    queryKey: queryKeys.tenantUsers.search(tenantId ?? "", trimmed),
    queryFn: async () => {
      const items = await searchTenantUsers(tenantId as string, {
        search: trimmed,
        limit,
      });
      return { items: items.map(mapTenantUserToSearchResult), query: trimmed };
    },
    enabled:
      enabled &&
      Boolean(tenantId) &&
      trimmed.length >= MIN_TENANT_USER_SEARCH_LENGTH,
    placeholderData: keepPreviousData,
  });
};
